import { supabase } from '../../lib/supabaseClient';
import {
  boardFrises,
  type FamilyCountRow,
} from '../../lib/dossiers/friseBoard';
import {
  slugifyParcoursName,
  type ParcoursFamily,
  type ParcoursFolder,
  type ParcoursFrise,
} from '../../lib/dossiers/parcoursDefaults';

const db = supabase as any;

export interface DossierFriseStep {
  position: number;
  label: string;
  effectiveOn: string | null;
}

export async function listDossierFrise(dossierId: string): Promise<{
  position: number;
  steps: DossierFriseStep[];
}> {
  const { data: dossier, error } = await db
    .from('dossiers')
    .select('current_frise_position')
    .eq('id', dossierId)
    .single();
  if (error) throw new Error(error.message);
  const { data: steps, error: stepError } = await db
    .from('dossier_frise_steps')
    .select('position, label, effective_on')
    .eq('dossier_id', dossierId)
    .order('position');
  if (stepError) throw new Error(stepError.message);
  return {
    position: dossier.current_frise_position as number,
    steps: (steps ?? []).map((step: { position: number; label: string; effective_on: string | null }) => ({
      position: step.position,
      label: step.label,
      effectiveOn: step.effective_on,
    })),
  };
}

export async function replaceDossierFrise(
  dossierId: string,
  friseId: string,
  position: number,
  effectiveOn: string,
): Promise<void> {
  const { error } = await db.rpc('replace_dossier_frise', {
    p_dossier_id: dossierId,
    p_frise_id: friseId,
    p_position: position,
    p_effective_on: effectiveOn,
  });
  if (error) throw new Error(error.message);
}

/** Remplace les étapes de CE dossier. Le modèle et les autres dossiers ne bougent pas. */
export async function editDossierFriseSteps(
  dossierId: string,
  steps: { from: number | null; label: string }[],
): Promise<void> {
  const { error } = await db.rpc('edit_dossier_frise_steps', {
    p_dossier_id: dossierId,
    p_steps: steps,
  });
  if (error) throw new Error(error.message);
}

export async function transitionFriseStep(
  dossierId: string,
  position: number,
  effectiveOn: string,
): Promise<void> {
  const { error } = await db.rpc('transition_frise_step', {
    p_dossier_id: dossierId,
    p_position: position,
    p_effective_on: effectiveOn,
  });
  if (error) throw new Error(error.message);
}

interface FamilyRow { id: string; slug: string; name: string; position: number }
interface FriseRow { id: string; family_id: string; slug: string; name: string; is_default: boolean; offered: boolean }
interface StepRow { template_id: string; position: number; label: string }
interface FolderRow { id: string; family_id: string; slug: string; name: string; is_default: boolean; offered: boolean }
interface ItemRow { template_id: string; position: number; name: string }

export async function loadParcoursCatalog(): Promise<ParcoursFamily[]> {
  const [families, frises, steps, folders, items] = await Promise.all([
    db.from('dossier_families').select('id, slug, name, position').order('position'),
    db.from('dossier_frise_templates').select('id, family_id, slug, name, is_default, offered').order('name'),
    db.from('dossier_frise_template_steps').select('template_id, position, label').order('position'),
    db.from('dossier_folder_templates').select('id, family_id, slug, name, is_default, offered').order('name'),
    db.from('dossier_folder_template_items').select('template_id, position, name').order('position'),
  ]);
  for (const result of [families, frises, steps, folders, items]) {
    if (result.error) throw new Error(result.error.message);
  }
  return (families.data as FamilyRow[]).map((family) => ({
    id: family.id,
    slug: family.slug,
    name: family.name,
    frises: (frises.data as FriseRow[])
      .filter((frise) => frise.family_id === family.id)
      .map((frise): ParcoursFrise => ({
        id: frise.id,
        slug: frise.slug,
        name: frise.name,
        isDefault: frise.is_default,
        offered: frise.offered,
        steps: (steps.data as StepRow[])
          .filter((step) => step.template_id === frise.id)
          .sort((a, b) => a.position - b.position)
          .map((step) => step.label),
      })),
    folders: (folders.data as FolderRow[])
      .filter((folder) => folder.family_id === family.id)
      .map((folder): ParcoursFolder => ({
        id: folder.id,
        slug: folder.slug,
        name: folder.name,
        isDefault: folder.is_default,
        offered: folder.offered,
        names: (items.data as ItemRow[])
          .filter((item) => item.template_id === folder.id)
          .sort((a, b) => a.position - b.position)
          .map((item) => item.name),
      })),
  }));
}

export async function listFamilyStepRows(leadCounselId?: string): Promise<FamilyCountRow[]> {
  let query = db
    .from('dossiers')
    .select('id, title, frise_template_id, current_frise_position')
    .is('deleted_at', null)
    .is('archived_at', null)
    .order('opened_at', { ascending: false });
  if (leadCounselId) query = query.eq('lead_counsel_id', leadCounselId);
  const [catalog, dossiers] = await Promise.all([loadParcoursCatalog(), query]);
  if (dossiers.error) throw new Error(dossiers.error.message);
  const frises = new Map(boardFrises(catalog).map((frise) => [frise.id, frise]));
  const rows: FamilyCountRow[] = [];
  for (const dossier of dossiers.data ?? []) {
    const frise = frises.get(dossier.frise_template_id as string);
    if (!frise) continue;
    const position = (dossier.current_frise_position as number) ?? 1;
    const step = frise.steps.find((item) => item.position === position);
    rows.push({
      familyName: frise.familyName,
      friseId: frise.id,
      friseName: frise.name,
      position,
      label: step?.label ?? `Étape ${position}`,
      title: dossier.title as string,
    });
  }
  return rows;
}

export async function countParcoursUsage(): Promise<{
  families: Record<string, number>;
  frises: Record<string, number>;
  folders: Record<string, number>;
}> {
  const { data, error } = await db
    .from('dossiers')
    .select('family_id, frise_template_id, folder_template_id');
  if (error) throw new Error(error.message);
  const families: Record<string, number> = {};
  const frises: Record<string, number> = {};
  const folders: Record<string, number> = {};
  for (const row of data ?? []) {
    if (row.family_id) families[row.family_id] = (families[row.family_id] ?? 0) + 1;
    if (row.frise_template_id) frises[row.frise_template_id] = (frises[row.frise_template_id] ?? 0) + 1;
    if (row.folder_template_id) folders[row.folder_template_id] = (folders[row.folder_template_id] ?? 0) + 1;
  }
  return { families, frises, folders };
}

async function uniqueSlug(table: string, base: string): Promise<string> {
  let slug = base;
  let n = 2;
  for (;;) {
    const { data } = await db.from(table).select('id').eq('slug', slug).maybeSingle();
    if (!data) return slug;
    slug = `${base}-${n}`;
    n += 1;
  }
}

export async function createFamily(name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Nom requis');
  const { data: last } = await db.from('dossier_families').select('position').order('position', { ascending: false }).limit(1);
  const position = (last?.[0]?.position ?? 0) + 1;
  const slug = await uniqueSlug('dossier_families', slugifyParcoursName(trimmed));
  const { error } = await db.from('dossier_families').insert({ slug, name: trimmed, position });
  if (error) throw new Error(error.message);
}

export async function renameFamily(id: string, name: string): Promise<void> {
  const { error } = await db.from('dossier_families').update({ name: name.trim() }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteFamily(id: string): Promise<void> {
  const { error } = await db.from('dossier_families').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

async function clearDefault(table: string, familyId: string): Promise<void> {
  const { error } = await db.from(table).update({ is_default: false }).eq('family_id', familyId);
  if (error) throw new Error(error.message);
}

export async function createFrise(familyId: string, name: string, steps: string[]): Promise<void> {
  const labels = steps.map((step) => step.trim()).filter(Boolean);
  if (labels.length < 2) throw new Error('Une frise a au moins deux étapes');
  const slug = await uniqueSlug('dossier_frise_templates', slugifyParcoursName(name.trim()));
  const { count } = await db
    .from('dossier_frise_templates')
    .select('id', { count: 'exact', head: true })
    .eq('family_id', familyId);
  const isDefault = (count ?? 0) === 0;
  const { data, error } = await db
    .from('dossier_frise_templates')
    .insert({ family_id: familyId, slug, name: name.trim(), is_default: isDefault, offered: true })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  const rows = labels.map((label, index) => ({ template_id: data.id, position: index + 1, label }));
  const inserted = await db.from('dossier_frise_template_steps').insert(rows);
  if (inserted.error) throw new Error(inserted.error.message);
}

export async function renameFrise(id: string, name: string): Promise<void> {
  const { error } = await db.from('dossier_frise_templates').update({ name: name.trim() }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function setFriseOffered(id: string, offered: boolean): Promise<void> {
  const { error } = await db.from('dossier_frise_templates').update({ offered }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function setDefaultFrise(familyId: string, id: string): Promise<void> {
  await clearDefault('dossier_frise_templates', familyId);
  const { error } = await db.from('dossier_frise_templates').update({ is_default: true }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function replaceFriseSteps(templateId: string, steps: string[]): Promise<void> {
  const labels = steps.map((step) => step.trim()).filter(Boolean);
  if (labels.length < 2) throw new Error('Une frise a au moins deux étapes');
  const removed = await db.from('dossier_frise_template_steps').delete().eq('template_id', templateId);
  if (removed.error) throw new Error(removed.error.message);
  const rows = labels.map((label, index) => ({ template_id: templateId, position: index + 1, label }));
  const inserted = await db.from('dossier_frise_template_steps').insert(rows);
  if (inserted.error) throw new Error(inserted.error.message);
}

export async function deleteFrise(id: string): Promise<void> {
  const { error } = await db.from('dossier_frise_templates').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function createFolderTemplate(familyId: string, name: string, names: string[]): Promise<void> {
  const labels = names.map((item) => item.trim()).filter(Boolean);
  if (labels.length < 1) throw new Error('Au moins un dossier');
  const slug = await uniqueSlug('dossier_folder_templates', slugifyParcoursName(name.trim()));
  const { count } = await db
    .from('dossier_folder_templates')
    .select('id', { count: 'exact', head: true })
    .eq('family_id', familyId);
  const isDefault = (count ?? 0) === 0;
  const { data, error } = await db
    .from('dossier_folder_templates')
    .insert({ family_id: familyId, slug, name: name.trim(), is_default: isDefault, offered: true })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  const rows = labels.map((label, index) => ({ template_id: data.id, position: index + 1, name: label }));
  const inserted = await db.from('dossier_folder_template_items').insert(rows);
  if (inserted.error) throw new Error(inserted.error.message);
}

export async function renameFolderTemplate(id: string, name: string): Promise<void> {
  const { error } = await db.from('dossier_folder_templates').update({ name: name.trim() }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function setFolderOffered(id: string, offered: boolean): Promise<void> {
  const { error } = await db.from('dossier_folder_templates').update({ offered }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function setDefaultFolder(familyId: string, id: string): Promise<void> {
  await clearDefault('dossier_folder_templates', familyId);
  const { error } = await db.from('dossier_folder_templates').update({ is_default: true }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function replaceFolderNames(templateId: string, names: string[]): Promise<void> {
  const labels = names.map((item) => item.trim()).filter(Boolean);
  if (labels.length < 1) throw new Error('Au moins un dossier');
  const removed = await db.from('dossier_folder_template_items').delete().eq('template_id', templateId);
  if (removed.error) throw new Error(removed.error.message);
  const rows = labels.map((name, index) => ({ template_id: templateId, position: index + 1, name }));
  const inserted = await db.from('dossier_folder_template_items').insert(rows);
  if (inserted.error) throw new Error(inserted.error.message);
}

export async function deleteFolderTemplate(id: string): Promise<void> {
  const { error } = await db.from('dossier_folder_templates').delete().eq('id', id);
  if (error) throw new Error(error.message);
}
